import { useEffect, useRef, useState } from 'react';

import { apiFetch } from '../lib/api.js';
import { usePolling } from './usePolling.js';

/** Matches the legacy widget's cadence — a badge is worth a minute of staleness. */
const POLL_MS = 60_000;

/**
 * A count the closed widget polls off the BFF.
 *
 * One engine rather than one hook per number, because the policy is the part
 * worth keeping identical: the widget now draws two counts side by side, and two
 * copies of "how often, when, and what a failure means" would drift into a badge
 * and a nudge that refresh at different moments in front of the employer.
 *
 * Every count behind it is answered server-side — no live Stream connection — so
 * an idle widget on every host page costs no Stream MAU and no concurrent
 * connection. That is also why this polls rather than subscribes: a subscription
 * means an open connection on every page the widget sits on, which is the exact
 * cost a closed widget exists to avoid.
 *
 * Failures keep the last known count rather than falling to zero — a transient
 * blip should not tell an employer that nothing is waiting on them. Null while
 * disabled or unanswered: a paused poll's last number is not a current one.
 */
export function usePolledCount(path: string, enabled: boolean): number | null {
  const [count, setCount] = useState<number | null>(null);

  const live = useRef(enabled);
  live.current = enabled;
  useEffect(() => {
    if (!enabled) setCount(null);
  }, [enabled]);

  usePolling(
    () => {
      apiFetch<{ count: number }>(path)
        .then(({ count }) => {
          if (live.current) setCount(count);
        })
        .catch(() => {
          /* transient — keep the last known count */
        });
    },
    enabled,
    POLL_MS,
  );

  return count;
}
